import PrimeTooltip from 'primevue/tooltip';

// Tooltip `class` value used by resources/js/utilities/longTitleTooltip.js.
// Only this is gated on truncation — other app tooltips are unaffected.
const TRUNCATION_GATED_CLASSES = new Set(['nav-title-tooltip']);

// Tooltip `class` value set by ETag.vue (E_TAG_TOUCH_TOOLTIP_CLASS) on touch chips.
// Only these tooltips are clamped to the viewport — every other app tooltip
// (including desktop ETag tooltips) keeps PrimeVue's stock placement.
const VIEWPORT_CLAMPED_CLASSES = new Set(['e-tag-touch-tooltip']);

const VIEWPORT_CLAMP_MARGIN_PX = 8;

/**
 * PrimeVue's top/bottom alignment can set a negative `left` when the tooltip is
 * wider than the trigger but still within the viewport (fallback right-aligns to
 * the host without clamping). Real iOS Safari shows this as ~64px off-screen.
 */
export function clampTooltipElementToViewport(tooltipEl) {
    if (!tooltipEl) {
        return;
    }

    const rect = tooltipEl.getBoundingClientRect();
    const maxRight = window.innerWidth - VIEWPORT_CLAMP_MARGIN_PX;
    let adjust = 0;

    if (rect.left < VIEWPORT_CLAMP_MARGIN_PX) {
        adjust = VIEWPORT_CLAMP_MARGIN_PX - rect.left;
    } else if (rect.right > maxRight) {
        adjust = maxRight - rect.right;
    }

    if (adjust === 0) {
        return;
    }

    const currentLeft = parseFloat(tooltipEl.style.left);

    if (Number.isFinite(currentLeft)) {
        tooltipEl.style.left = `${currentLeft + adjust}px`;
    }
}

function usesViewportClamp(binding) {
    const tooltipClass = binding?.value?.class;

    if (typeof tooltipClass !== 'string') {
        return false;
    }

    return tooltipClass.split(/\s+/).some(className => VIEWPORT_CLAMPED_CLASSES.has(className));
}

function cancelViewportClamp(el) {
    if (el.$_viewportClampTimer == null && el.$_viewportClampFrame == null) {
        return;
    }

    window.clearTimeout(el.$_viewportClampTimer);
    cancelAnimationFrame(el.$_viewportClampFrame);
    el.$_viewportClampTimer = null;
    el.$_viewportClampFrame = null;
}

function scheduleViewportClamp(el) {
    const showDelay = el.$_ptooltipShowDelay ?? 0;

    cancelViewportClamp(el);

    el.$_viewportClampTimer = window.setTimeout(() => {
        el.$_viewportClampTimer = null;
        el.$_viewportClampFrame = requestAnimationFrame(() => {
            el.$_viewportClampFrame = null;

            const tooltipEl = el.$_ptooltipId ? document.getElementById(el.$_ptooltipId) : null;

            clampTooltipElementToViewport(tooltipEl);
        });
    }, showDelay);
}

function attachViewportClamp(el, binding) {
    if (!usesViewportClamp(binding)) {
        return;
    }

    // PrimeTooltip.updated() unbinds and rebinds listeners; wrap whatever handler is
    // current so clamping survives chip list re-renders (e.g. filter changes).
    for (const [key, eventName] of [
        ['$_ptooltipMouseEnterEvent', 'mouseenter'],
        ['$_ptooltipFocusEvent', 'focus'],
    ]) {
        const original = el[key];

        if (!original || original.$_viewportClampWrapped) {
            continue;
        }

        el.removeEventListener(eventName, original);

        const wrapped = function viewportClampWrappedListener(...args) {
            original.apply(this, args);
            scheduleViewportClamp(el);
        };
        wrapped.$_viewportClampWrapped = true;

        el.addEventListener(eventName, wrapped);
        el[key] = wrapped;
    }
}

function isOverflowing(el) {
    return el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
}

/**
 * Wrap the native mouseenter/focus listener PrimeVue just attached so it only
 * shows the tooltip when the label is actually clamped. Layout isn't settled
 * yet when the directive binds, so this checks at trigger time instead of
 * pre-computing truncation.
 */
function gateShowOnTruncation(el, binding) {
    if (!TRUNCATION_GATED_CLASSES.has(binding.value?.class)) {
        return;
    }

    for (const [key, eventName] of [
        ['$_ptooltipMouseEnterEvent', 'mouseenter'],
        ['$_ptooltipFocusEvent', 'focus'],
    ]) {
        const original = el[key];

        if (!original || original.$_truncationGated) {
            continue;
        }

        el.removeEventListener(eventName, original);

        const gated = function truncationGatedListener(event) {
            if (isOverflowing(el)) {
                original.call(this, event);
            }
        };
        gated.$_truncationGated = true;

        el.addEventListener(eventName, gated);
        el[key] = gated;
    }
}

/**
 * PrimeVue's tooltip directive removes overlay nodes on unmount. When many
 * sidebar nav items re-render at once (course menu tree), that cleanup can race
 * Vue's DOM teardown and throw NotFoundError on removeChild. Swallow that case.
 */
export default {
    ...PrimeTooltip,
    beforeMount(el, binding, ...args) {
        PrimeTooltip.beforeMount(el, binding, ...args);
        gateShowOnTruncation(el, binding);
        attachViewportClamp(el, binding);
    },
    updated(el, binding, ...args) {
        PrimeTooltip.updated(el, binding, ...args);
        gateShowOnTruncation(el, binding);
        attachViewportClamp(el, binding);
    },
    unmounted(el, binding, vnode, prevVnode) {
        cancelViewportClamp(el);

        try {
            PrimeTooltip.unmounted(el, binding, vnode, prevVnode);
        } catch (error) {
            if (error?.name !== 'NotFoundError') {
                throw error;
            }
        }
    },
};
